import {
  GraphQLBoolean,
  GraphQLID,
  GraphQLList,
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLString
} from 'graphql'
import { Context } from '../shared'
import {
  normalizeApplicationLanguage,
  transformPolicies,
  transformVulnerabilityBreakdown
} from './helpers/application'
import { ContrastGraphQLFieldConfig } from '../index'
import {
  applicationQueryArgs,
  ApplicationQueryArgs
} from '../types/application-query-args'
import {
  VulnerabilitySeverityBreakdown,
  vulnerabilitySeverityBreakdown
} from '../types/vulnerability-severity-breakdown'
import { AttackStatus, AttackStatusType } from '../types/attack-status'
import {
  BaseCompliancePolicy,
  BaseCompliancePolicyType
} from '../types/compliance-policy'
import { serverEnvironmentType } from '../types/server'

const VIEW = 'view'
const PRESENTATION = 'presentation'
const SERVICE = 'service'

interface ApplicationLibrary {
  readonly name: string
  readonly hash: string
  readonly vulnerable: boolean
}

const applicationLibrary = new GraphQLObjectType({
  name: 'ApplicationLibrary',
  description: 'Application Library data',
  fields: () => ({
    name: {
      type: new GraphQLNonNull(GraphQLString)
    },
    hash: {
      type: new GraphQLNonNull(GraphQLString)
    },
    vulnerable: {
      type: new GraphQLNonNull(GraphQLBoolean)
    }
  })
})

interface ApplicationTechnology {
  readonly name: string
  readonly libraries: ApplicationLibrary[]
}

const applicationTechnology = new GraphQLObjectType({
  name: 'ApplicationTechnology',
  description: 'Application Technology details',
  fields: () => ({
    name: {
      type: new GraphQLNonNull(GraphQLString)
    },
    libraries: {
      type: new GraphQLNonNull(new GraphQLList(applicationLibrary))
    }
  })
})

interface TechnologiesBreakdown {
  readonly [VIEW]: ApplicationTechnology[]
  readonly [PRESENTATION]: ApplicationTechnology[]
  readonly [SERVICE]: ApplicationTechnology[]
}

const technologiesBreakdown = new GraphQLObjectType({
  name: 'TechnologiesBreakdown',
  description: 'Breakdown of a set of application technologies',
  fields: () => ({
    [VIEW]: {
      type: new GraphQLNonNull(new GraphQLList(applicationTechnology))
    },
    [PRESENTATION]: {
      type: new GraphQLNonNull(new GraphQLList(applicationTechnology))
    },
    [SERVICE]: {
      type: new GraphQLNonNull(new GraphQLList(applicationTechnology))
    }
  })
})

interface ApplicationArchitecture {
  readonly id: string
  readonly language: string
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly attackStatus?: AttackStatus
  readonly hasAssess: boolean
  readonly hasAProtectedServer: boolean
  readonly online: boolean
  readonly violatedCompliancePolicies: BaseCompliancePolicy[]

  readonly serverEnvironments: string[]
  readonly technologiesBreakdown: TechnologiesBreakdown
}

const applicationArchitectureType = new GraphQLObjectType({
  name: 'ApplicationArchitecture',
  fields: () => ({
    id: {
      description: 'The unique ID for an application',
      type: new GraphQLNonNull(GraphQLID)
    },
    language: {
      description:
        'The programming language in which the application was written',
      type: new GraphQLNonNull(GraphQLString)
    },
    vulnerabilityBreakdown: {
      description: 'Vulnerability breakdown by severity',
      type: new GraphQLNonNull(vulnerabilitySeverityBreakdown)
    },
    attackStatus: {
      description: 'The current attack status',
      type: AttackStatusType
    },
    hasAssess: {
      description: 'Indicates if the application has Assess feature enabled.',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    hasAProtectedServer: {
      description:
        'Indicates if the application has at least one protected server.',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    online: {
      description: 'Is the application currently online',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    violatedCompliancePolicies: {
      description:
        'A collection of compliance policies that this application violates',
      type: new GraphQLList(new GraphQLNonNull(BaseCompliancePolicyType))
    },

    serverEnvironments: {
      description: 'Server environments for the given application',
      type: new GraphQLNonNull(new GraphQLList(serverEnvironmentType))
    },
    technologiesBreakdown: {
      description: 'Application technologies breakdown',
      type: new GraphQLNonNull(technologiesBreakdown)
    }
  })
})

export const applicationArchitecture: ContrastGraphQLFieldConfig<
  any,
  Context,
  ApplicationQueryArgs,
  ApplicationArchitecture
> = {
  description: 'Get the application architecture details',
  type: new GraphQLNonNull(applicationArchitectureType),
  args: applicationQueryArgs,
  async resolve(_source, args, context) {
    const { organizationID, applicationID } = args

    const { application } = await context.connector.get(
      `/${organizationID}/flowmap/${applicationID}`
    )

    return normalizeApplicationArchitecture(application)
  }
}

export const normalizeApplicationArchitecture = ({
  app_id: id,
  language,
  trace_severity_breakdown,
  assess,
  defend,
  attack_status,
  status,
  policies,

  server_environments,
  architecture_techs
}): ApplicationArchitecture => ({
  id,
  language: normalizeApplicationLanguage(language),
  vulnerabilityBreakdown: transformVulnerabilityBreakdown(
    trace_severity_breakdown
  ) as VulnerabilitySeverityBreakdown,
  hasAssess: !!assess,
  hasAProtectedServer: !!defend,
  attackStatus: attack_status,
  online: status === 'online',
  violatedCompliancePolicies: transformPolicies(policies),

  serverEnvironments: server_environments || [],
  technologiesBreakdown: architecture_techs
})
